(() => {
    "use strict";

    const $ = jQuery;

    const PLUGIN_NAME = "jqueryUploader";
    const DEFAULT_OPTIONS_KEY = "defaultUploaderOptions";
    const PREVIEW_PROGRESS_SELECTOR = ".jquery-uploader-preview-progress";

    const FILE_TYPE_IMAGE = "image";
    const FILE_TYPE_OTHER = "other";
    const IMAGE_EXTENSIONS = ["jpg", "png", "jpeg", "gif", "bmp"];

    const BlobUrlManager = (() => {
        const URL = window.URL || window.webkitURL;
        const blobUrlMap = new Map();

        return {
            createBlobUrl(file) {
                const url = URL.createObjectURL(file);
                blobUrlMap.set(url, file);
                return url;
            },
            revokeBlobUrl(url) {
                URL.revokeObjectURL(url);
                blobUrlMap.delete(url);
            },
            getBlobFromUrl(url) {
                return blobUrlMap.get(url);
            }
        };
    })();

    function generateUUID() {
        const hex = "0123456789abcdef";
        let uuid = [];

        for (let i = 0; i < 36; i++) {
            uuid[i] = hex[Math.floor(Math.random() * 16)];
        }

        uuid[14] = "4";
        uuid[19] = hex[(parseInt(uuid[19], 16) & 0x3) | 0x8];
        [8, 13, 18, 23].forEach(pos => uuid[pos] = "-");

        return uuid.join("");
    }

    function detectFileType(url) {
        if (!url) return FILE_TYPE_OTHER;
        if (url.startsWith("blob")) {
            const blob = BlobUrlManager.getBlobFromUrl(url);
            return blob && blob.type.includes("image") ? FILE_TYPE_IMAGE : FILE_TYPE_OTHER;
        }

        for (const ext of IMAGE_EXTENSIONS) {
            if (url.endsWith(ext)) return FILE_TYPE_IMAGE;
        }

        return FILE_TYPE_OTHER;
    }

    class Uploader {
        constructor($input, options) {
            this.$input = $input;
            const globalDefaults = window[DEFAULT_OPTIONS_KEY] || {};
            this.options = $.extend(true, {}, Uploader.defaults, globalDefaults, options);

            this.files = [];

            this.validateOptions();
            this.initDOM();
            this.initEvents();
        }

        validateOptions() {
            if (this.options.mode !== Uploader.mode.url) {
                throw new Error(`Invalid mode: ${this.options.mode}`);
            }

            if (this.$input.attr("type") && this.$input.attr("type") !== "text") {
                throw new Error(`URL / TEXT input required, found: ${this.$input.attr("type")}`);
            }
        }

        initDOM() {
            this.$input.hide();

            this.$selectCard = $(`
                <div class="jquery-uploader-select-card">
                    <div class="jquery-uploader-select">
                        <div class="upload-button">
                            <i class="fa fa-plus"></i><br/>
                            <a>Upload</a>
                        </div>
                    </div>
                </div>
            `);

            this.$previewContainer = $('<div class="jquery-uploader-preview-container"></div>');
            this.$wrapper = $('<div class="jquery-uploader"></div>').append(this.$previewContainer);

            (this.options.parent || this.$input.parent()).append(this.$wrapper);

            if (this.options.mode === Uploader.mode.url) {
                this.createInitialFiles(this.$input.val());
            }

            this.$input.trigger("uploader-init");
        }

        createFileCard(id, url, type = detectFileType(url)) {
            const content = type === FILE_TYPE_IMAGE
                ? `<img alt="preview" class="files_img" src="${url}"/>`
                : '<div class="file_other"></div>';
            const $card = $(`
                <div class="jquery-uploader-card" id="${id}">
                    <div class="jquery-uploader-preview-main">
                        <div class="jquery-uploader-preview-action">
                            <ul>
                                <li class="file-delete"><i class="fa fa-trash"></i></li>
                            </ul>
                        </div>
                        <div class="jquery-uploader-preview-progress">
                            <div class="progress-mask"></div>
                            <div class="progress-loading"><i class="fa fa-spinner fa-spin"></i></div>
                        </div>
                        ${content}
                    </div>
                </div>
            `);

            $card.find(PREVIEW_PROGRESS_SELECTOR).hide();
            return $card;
        }

        updateCardStyle($card, style) {
            const styles = {
                warning: "0px 0px 3px 1px #f8ac59 inset",
                error: "0px 0px 3px 1px #ed5565 inset",
                default: ""
            };
            $card.css("box-shadow", styles[style]);
        }

        upload() {
            this.processUpload();
            return this.$input;
        }

        clean() {
            this.files = [];
            this.refreshPreview();
            this.updateInputValue();
            return this.$input;
        }

        uploadFiles() {
            return this.files;
        }

        initEvents() {
            // placeholder for external extension
        }

        updateInputValue() {
            const formatter = this.options.inputValueFormatter;

            const urls = this.files
                .filter(f => f.status === Uploader.fileStatus.uploaded || f.status === Uploader.fileStatus.initial)
                .map(f => typeof formatter === 'function' ? formatter(f) : f.url);

            const current = this.$input.val();
            const updated = JSON.stringify(urls);

            if (current !== updated) {
                this.$input.val(updated).trigger("change");
            }
        }

        refreshPreview() {
            this.$previewContainer.empty();

            this.files.forEach(file => {
                this.$previewContainer.append(file.$ele);
                file.$ele.find(".file-delete").on("click", this.handleDelete.bind(this));
            });

            if (this.options.multiple || this.files.length === 0) {
                this.$previewContainer.append(this.$selectCard);
                this.$selectCard.off("click").on("click", this.selectFile.bind(this));
            }
        }

        createInitialFiles(value) {
            let defaultFiles = [];

            if (this.options.defaultValue) {
                defaultFiles = this.options.defaultValue;
            } else if (value) {
                let values;

                try {
                    // ✅ Try parsing JSON first
                    const parsed = JSON.parse(value);
                    values = Array.isArray(parsed) ? parsed : [parsed];
                } catch (e) {
                    // fallback to old behavior
                    values = this.options.multiple ? value.split(",") : [value];
                }

                defaultFiles = values.map((url, idx) => ({
                    name: `default${idx}`,
                    type: detectFileType(url),
                    url
                }));
            }

            defaultFiles.forEach(file => {
                const id = generateUUID();
                const $card = this.createFileCard(id, file.url, file.type);
                this.files.push({
                    id,
                    type: file.type,
                    name: file.name,
                    url: file.url,
                    status: Uploader.fileStatus.initial,
                    file: null,
                    $ele: $card
                });
            });

            this.refreshPreview();
            this.updateInputValue();
        }

        onUploadProgress(file, percent) {
            file.$ele.find(".progress-mask").css("height", `${100 - percent}%`);
        }

        onUploadSuccess(file, response) {
            file.$ele.find(PREVIEW_PROGRESS_SELECTOR).hide();
            file.$ele.find(".jquery-uploader-preview-action").show();

            let parsed;
            try {
                parsed = this.options.ajaxConfig.responseConverter(file, response);
                if (!parsed.url) {
                    return this.onUploadError(file, parsed);
                }
            } catch (error) {
                console.error("Upload parsing error", error);
                return this.onUploadError(file, "Unexpected error");
            }

            if (file.url.startsWith("blob")) {
                BlobUrlManager.revokeBlobUrl(file.url);
            }

            file.name = parsed.name || file.name;
            file.url = parsed.url;
            file.status = Uploader.fileStatus.uploaded;
            file.$ele.find("img").attr("src", file.url);

            this.refreshPreview();
            this.updateInputValue();
            this.$input.trigger("upload-success", file, response);
        }

        onUploadError(file, error) {
            console.error("Upload error", file, error);
            file.$ele.find(PREVIEW_PROGRESS_SELECTOR).hide();
            file.$ele.attr("title", error);
            this.updateCardStyle(file.$ele, "error");
            this.$input.trigger("upload-error", file, error);
        }

        selectFile() {
            $('<input type="file" ' + (this.options.multiple ? "multiple" : "") + ' />')
                .on("change", (e) => this.addFiles(e.target.files))
                .click();
        }

        addFiles(files) {
            const addedFiles = [];

            for (const file of files) {
                const type = file.type.includes("image") ? FILE_TYPE_IMAGE : FILE_TYPE_OTHER;
                const blobUrl = BlobUrlManager.createBlobUrl(file);
                const id = generateUUID();
                const $card = this.createFileCard(id, blobUrl, type);

                this.updateCardStyle($card, "warning");

                addedFiles.push({
                    id,
                    type,
                    name: file.name,
                    url: blobUrl,
                    status: Uploader.fileStatus.selected,
                    file,
                    $ele: $card
                });
            }

            this.files.push(...addedFiles);
            this.refreshPreview();
            this.$input.trigger("file-add", addedFiles);

            if (this.options.autoUpload) {
                this.processUpload();
            }
        }

        processUpload() {
            const pendingFiles = this.files.filter(f => f.status === Uploader.fileStatus.selected);

            this.$input.trigger("before-upload", pendingFiles);

            pendingFiles.forEach(file => {
                file.$ele.find(PREVIEW_PROGRESS_SELECTOR).show();
                this.updateCardStyle(file.$ele, "default");

                try {
                    this.options.ajaxConfig.ajaxRequester(
                        this.options.ajaxConfig,
                        file,
                        (progress) => this.onUploadProgress(file, progress),
                        (response) => this.onUploadSuccess(file, response),
                        (error) => this.onUploadError(file, error)
                    );
                } catch (error) {
                    this.onUploadError(file, "AJAX Request Error");
                }
            });
        }

        handleDelete(event) {
            const $card = $(event.target).closest(".jquery-uploader-card");
            const id = $card.attr("id");

            const index = this.files.findIndex(f => f.id === id);
            if (index > -1) {
                const [file] = this.files.splice(index, 1);
                if (file.url.startsWith("blob")) {
                    BlobUrlManager.revokeBlobUrl(file.url);
                }
                $card.remove();
                this.$input.trigger("file-remove", file);
                this.updateInputValue();
                this.refreshPreview();
            }
        }
    }

    // Static configs
    Uploader.config = {
        paramsBuilder(file) {
            const formData = new FormData();
            formData.append("file", file.file);
            return formData;
        },
        responseConverter(file, response) {
            return { url: response.data, name: null };
        },
        ajaxRequester(config, file, onProgress, onSuccess, onError) {
            $.ajax({
                url: config.url,
                method: config.method,
                contentType: false,
                processData: false,
                data: config.paramsBuilder(file),
                success: onSuccess,
                error: () => onError("Upload failed"),
                xhr() {
                    const xhr = new XMLHttpRequest();
                    xhr.upload.addEventListener("progress", (e) => {
                        if (e.lengthComputable) {
                            const percent = (e.loaded / e.total) * 100;
                            onProgress(percent);
                        }
                    });
                    return xhr;
                }
            });
        }
    };

    Uploader.mode = { url: "url", file: "file", custom: "custom" };
    Uploader.fileStatus = { selected: "selected", uploading: "uploading", uploaded: "uploaded", error: "error", initial: "initial" };

    Uploader.defaults = {
        mode: Uploader.mode.url,
        multiple: false,
        defaultValue: null,
        parent: null,
        allowFileExt: "*",
        autoUpload: true,
        ajaxConfig: {
            url: "",
            method: "post",
            paramsBuilder: Uploader.config.paramsBuilder,
            ajaxRequester: Uploader.config.ajaxRequester,
            responseConverter: Uploader.config.responseConverter
        },
        inputValueFormatter: file => file.name,
    };

    // jQuery plugin definition
    $.fn.uploader = function (methodOrOptions, ...args) {
        if (typeof methodOrOptions === "string") {
            const instance = $(this[0]).data(PLUGIN_NAME);
            if (!instance) throw new Error("This is not a uploader instance");
            return instance[methodOrOptions](...args);
        }

        return this.each(function () {
            const $element = $(this);
            if (!$element.data(PLUGIN_NAME)) {
                const instance = new Uploader($element, methodOrOptions);
                $element.data(PLUGIN_NAME, instance);
            }
        });
    };

    $.fn.uploaderSetup = function (options) {
        window[DEFAULT_OPTIONS_KEY] = options;
    };

    $.Uploader = Uploader;
})();
